import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  saveAccessToken,
  saveRefreshToken,
} from "@/lib/storage";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8031/api";

function publicOrdersLabPath(path: string): string {
  // Only this DEV test storefront uses the synthetic API. General stores,
  // account sessions, Food and production keep their original API requests.
  if (typeof window === "undefined" || window.location.hostname !== "dev.fornecedor.net"
    || !window.location.pathname.startsWith("/homologacao/varejo")) return path;
  const eligible = /^\/(?:retail\/public\/|orders\/public\/homologacao-orders\/|catalog\/public\/stores\/homologacao-orders\/)/.test(path);
  return eligible ? `/retail/homologation${path}` : path;
}

type ApiRequestOptions = RequestInit & {
  authenticated?: boolean;
  retryOnUnauthorized?: boolean;
  responseType?: "json" | "blob";
};

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type");

  if (response.status === 204) {
    return null;
  }

  if (contentType?.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
}

async function refreshAccessToken(signal?: AbortSignal | null): Promise<string | null> {
  const refresh = getRefreshToken();

  if (!refresh) {
    return null;
  }

  const response = await fetch(`${API_URL}/auth/token/refresh/`, {
    signal,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    if (response.status >= 500 || response.status === 429)
      throw new ApiError("Não foi possível renovar a sessão agora. Tente novamente.", response.status, null);
    clearAuthTokens();
    return null;
  }

  const data = (await response.json()) as {
    access?: string;
    refresh?: string;
  };

  if (!data.access) {
    clearAuthTokens();
    return null;
  }

  saveAccessToken(data.access);

  if (data.refresh) {
    saveRefreshToken(data.refresh);
  }

  return data.access;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    authenticated = false,
    retryOnUnauthorized = true,
    responseType = "json",
    headers,
    ...requestOptions
  } = options;

  const requestHeaders = new Headers(headers);

  if (
    requestOptions.body &&
    !(requestOptions.body instanceof FormData) &&
    !requestHeaders.has("Content-Type")
  ) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (authenticated) {
    const accessToken = getAccessToken();

    if (accessToken) {
      requestHeaders.set("Authorization", `Bearer ${accessToken}`);
    }
  }

  const response = await fetch(`${API_URL}${publicOrdersLabPath(path)}`, {
    ...requestOptions,
    headers: requestHeaders,
  });

  if (
    response.status === 401 &&
    authenticated &&
    retryOnUnauthorized
  ) {
    const newAccessToken = await refreshAccessToken(requestOptions.signal);

    if (newAccessToken) {
      return apiRequest<T>(path, {
        ...options,
        retryOnUnauthorized: false,
      });
    }
  }

  const data =
    response.ok && responseType === "blob"
      ? await response.blob()
      : await parseResponse(response);

  if (!response.ok) {
    let message = "Não foi possível concluir a solicitação.";

    if (
      data &&
      typeof data === "object" &&
      "detail" in data &&
      typeof data.detail === "string"
    ) {
      message = data.detail;
    }

    throw new ApiError(message, response.status, data);
  }

  return data as T;
}
